import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Sparkles, LogOut, Loader2, Plus, Camera, Check, X, Trash2, Undo2,
  TrendingUp, Download, Mail, AlertTriangle, RefreshCw, FileText, Pencil,
} from 'lucide-react';
import { fileToUpload } from '../utils/docUpload';
import {
  summarise, preStartExpenses, gstWatch, availableFys, fyRange, fyLabel, currentFy, fyOf,
  money, sum, EXPENSE_CATEGORIES, PAYMENT_METHODS, TRADING_START,
} from '../utils/nailsBooks';
import { exportBooks, workbookBase64, booksFilename } from '../utils/nailsExport';

// ── Nails by Asha ─────────────────────────────────────────────────────────────
// A standalone portal (outside the admin <Layout>) for a SEPARATE business: Asha
// Bouveng, sole trader, her own ABN, not registered for GST. It is deliberately not a
// page of the engine — it is a tenant with its own front door, its own session cookie
// (hp_nails, set by nails-auth) and its own two-name guest list. Asha can never reach a
// Hollypaul page from here, and nobody at Hollypaul but Paul can reach this.
//
// The job it has to do: record what she earns, keep the receipt for what she spends,
// and hand the accountant a clean set of books at the end of the year. Everything is
// built for a phone held in one hand between clients.

const API = '/.netlify/functions/nails-sb';
const AUTH = '/.netlify/functions/nails-auth';
const SESSION_KEY = 'hp_nails_session';
const ACCENT = '#E9A0B6';   // hers, not Hollypaul's gold — this is her business
const GOOD = '#5FCf9b';
const WARN = '#E8B54A';
const BAD = '#E86A6A';

// LOCAL calendar date, never toISOString(). Sydney is UTC+10/+11, so between midnight
// and 10am toISOString() still reports YESTERDAY — which would have quietly dated a
// morning client's job, and every one-tap range below, a day early. Same shape as
// todayISO() in utils/periods.js.
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => isoLocal(new Date());
const dateAu = (s) => {
  const t = (s || '').toString().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(0, 4)}` : '';
};
// One-tap ranges for the custom picker — the periods actually asked about out loud
// ("how did last month go?"), so nobody has to work out which day a quarter ends on.
const monthRange = (back) => {
  const n = new Date();
  const start = new Date(n.getFullYear(), n.getMonth() - back, 1);
  const end = new Date(n.getFullYear(), n.getMonth() - back + 1, 0);
  return { from: isoLocal(start), to: isoLocal(end) };
};
const QUICK_RANGES = [
  { label: 'This month', range: () => monthRange(0) },
  { label: 'Last month', range: () => monthRange(1) },
  { label: 'Last 3 months', range: () => ({ from: monthRange(2).from, to: monthRange(0).to }) },
  { label: 'This quarter', range: () => {
    const n = new Date();
    const qStart = new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1);
    const qEnd = new Date(qStart.getFullYear(), qStart.getMonth() + 3, 0);
    return { from: isoLocal(qStart), to: isoLocal(qEnd) };
  } },
];

const monthName = (ym) => {
  const [y, m] = (ym || '').split('-');
  return y ? new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }) : ym;
};

const S = {
  page: { minHeight: '100vh', background: 'var(--hp-black, #101014)', color: 'var(--hp-text, #EDEDF0)', fontFamily: 'var(--font-body)', paddingBottom: 40 },
  wrap: { maxWidth: 720, margin: '0 auto', padding: '0 16px' },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--hp-border, #2A2A32)', background: 'var(--hp-surface, #17171C)', position: 'sticky', top: 0, zIndex: 20 },
  brand: { fontFamily: 'var(--font-display)', fontSize: '1.2rem', letterSpacing: '.03em', color: ACCENT, margin: 0, lineHeight: 1.1 },
  label: { display: 'block', fontSize: '.68rem', fontWeight: 700, color: 'var(--hp-dim, #8A8A96)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 },
  input: { width: '100%', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border, #2A2A32)', borderRadius: 10, color: 'var(--hp-text, #EDEDF0)', fontSize: '16px', padding: '13px 14px', marginBottom: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' },
  card: { background: 'var(--hp-surface, #17171C)', border: '1px solid var(--hp-border, #2A2A32)', borderRadius: 14, padding: 18, marginBottom: 14 },
  bigBtn: (bg, fg = '#141418') => ({ width: '100%', padding: 16, background: bg, color: fg, fontWeight: 800, fontSize: '1rem', border: 'none', borderRadius: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'var(--font-body)' }),
  ghost: { padding: '11px 14px', background: 'transparent', color: 'var(--hp-dim, #8A8A96)', border: '1px solid var(--hp-border, #2A2A32)', borderRadius: 10, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)', fontSize: '.85rem' },
  chip: (on) => ({ padding: '9px 13px', borderRadius: 999, border: `1px solid ${on ? ACCENT : 'var(--hp-border, #2A2A32)'}`, background: on ? ACCENT : 'transparent', color: on ? '#141418' : 'var(--hp-dim, #8A8A96)', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }),
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', borderTop: '1px solid var(--hp-border, #2A2A32)' },
  dim: { color: 'var(--hp-dim, #8A8A96)', fontSize: '.8rem' },
  amount: { fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, whiteSpace: 'nowrap' },
  h2: { fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: '0 0 4px', letterSpacing: '.02em' },
  sub: { fontSize: '.8rem', color: 'var(--hp-dim, #8A8A96)', margin: '0 0 14px', lineHeight: 1.5 },
};

const post = (action, payload = {}) =>
  fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then((r) => r.json());

// ══════════════════════════════════════════════════════════════════════════════
export default function NailsPortalPage() {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState('jobs');
  const [books, setBooks] = useState({ income: [], expenses: [], accountantEmail: '' });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const say = useCallback((text, kind = 'ok') => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await post('loadBooks');
      if (r.success) setBooks({ income: r.income || [], expenses: r.expenses || [], accountantEmail: r.accountantEmail || '' });
      else say(r.error || 'Could not load the books', 'bad');
    } catch (e) { say(e.message, 'bad'); }
    setLoading(false);
  }, [say]);

  // A valid cookie skips the PIN screen — and Paul's admin session is recognised here
  // too, so he never carries a second credential for his daughter's books.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await fetch(AUTH, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'refresh' }) }).then((x) => x.json());
        if (live && r.ok) { setSession({ login: r.login, name: r.name }); localStorage.setItem(SESSION_KEY, JSON.stringify({ login: r.login, name: r.name })); }
        else if (live) localStorage.removeItem(SESSION_KEY);
      } catch { /* offline — fall through to the sign-in screen */ }
      if (live) setChecking(false);
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => { if (session) reload(); }, [session, reload]);

  async function signOut() {
    await fetch(AUTH, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'signOut' }) }).catch(() => {});
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
  }

  if (checking) {
    return <div style={{ ...S.page, display: 'grid', placeItems: 'center' }}><Loader2 size={28} className="spin" style={{ color: ACCENT }} /></div>;
  }
  if (!session) return <SignIn onIn={(s) => { setSession(s); localStorage.setItem(SESSION_KEY, JSON.stringify(s)); }} />;

  return (
    <div style={S.page}>
      <header style={S.header}>
        <Sparkles size={22} style={{ color: ACCENT, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={S.brand}>Nails by Asha</h1>
          <div style={{ ...S.dim, fontSize: '.7rem' }}>Signed in as {session.name}</div>
        </div>
        <button onClick={reload} style={{ ...S.ghost, padding: '9px 11px' }} title="Refresh">
          {loading ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />}
        </button>
        <button onClick={signOut} style={{ ...S.ghost, padding: '9px 11px' }} title="Sign out"><LogOut size={15} /></button>
      </header>

      <div style={S.wrap}>
        <nav style={{ display: 'flex', gap: 8, margin: '16px 0' }}>
          {[['jobs', 'Jobs'], ['spending', 'Spending'], ['reports', 'Reports']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} style={{ ...S.chip(tab === k), flex: 1, padding: '11px 0' }}>{label}</button>
          ))}
        </nav>

        {toast && (
          <div style={{ ...S.card, padding: '12px 14px', borderColor: toast.kind === 'bad' ? BAD : GOOD, display: 'flex', alignItems: 'center', gap: 9, fontSize: '.86rem' }}>
            {toast.kind === 'bad' ? <AlertTriangle size={16} style={{ color: BAD, flexShrink: 0 }} /> : <Check size={16} style={{ color: GOOD, flexShrink: 0 }} />}
            <span>{toast.text}</span>
          </div>
        )}

        {tab === 'jobs' && <JobsTab books={books} reload={reload} say={say} />}
        {tab === 'spending' && <SpendingTab books={books} reload={reload} say={say} />}
        {tab === 'reports' && <ReportsTab books={books} say={say} reload={reload} />}
      </div>
      <style>{`.spin{animation:nbspin 1s linear infinite}@keyframes nbspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
function SignIn({ onIn }) {
  const [form, setForm] = useState({ login: 'ASHA', pin: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function go(e) {
    e?.preventDefault();
    setError(''); setBusy(true);
    try {
      const r = await fetch(AUTH, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }).then((x) => x.json());
      if (!r.ok) throw new Error(r.error || 'Incorrect name or PIN.');
      onIn({ login: r.login, name: r.name });
    } catch (e2) { setError(e2.message); }
    setBusy(false);
  }

  return (
    <div style={{ ...S.page, display: 'grid', placeItems: 'center', padding: 20 }}>
      <form onSubmit={go} style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <Sparkles size={34} style={{ color: ACCENT }} />
          <h1 style={{ ...S.brand, fontSize: '1.7rem', marginTop: 10 }}>Nails by Asha</h1>
          <p style={{ ...S.dim, marginTop: 6 }}>Income, expenses and receipts</p>
        </div>
        <div style={S.card}>
          <label style={S.label} htmlFor="nb-who">Who are you</label>
          <select id="nb-who" style={S.input} value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })}>
            <option value="ASHA">Asha</option>
            <option value="BOUVENGP2">Paul</option>
          </select>
          <label style={S.label} htmlFor="nb-pin">PIN</label>
          <input id="nb-pin" style={S.input} type="password" inputMode="numeric" autoComplete="current-password"
            value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} placeholder="••••" />
          {error && <p style={{ color: BAD, fontSize: '.84rem', margin: '0 0 12px' }}>{error}</p>}
          <button type="submit" disabled={busy} style={S.bigBtn(ACCENT)}>
            {busy ? <Loader2 size={17} className="spin" /> : <Check size={17} />} Sign in
          </button>
        </div>
      </form>
      <style>{`.spin{animation:nbspin 1s linear infinite}@keyframes nbspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Jobs — what she earned
function JobsTab({ books, reload, say }) {
  const [adding, setAdding] = useState(false);
  const blank = { jobDate: today(), client: '', description: '', amount: '', paymentMethod: 'cash', note: '' };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  // Editing reuses the same form: a job typed wrong should be correctable, not
  // deleted and retyped. `form.id` is what makes the save an update.
  const openEdit = (r) => {
    setForm({ id: r.id, jobDate: r.jobDate, client: r.client, description: r.description, amount: String(r.amount ?? ''), paymentMethod: r.paymentMethod, note: r.note });
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const rows = books.income;
  const todayTotal = sum(rows.filter((r) => r.jobDate === today()), (r) => r.amount);
  const fy = currentFy();
  const watch = gstWatch(rows, fy);

  async function save() {
    if (!form.amount) { say('How much was it?', 'bad'); return; }
    setBusy(true);
    const r = await post('saveIncome', form);
    setBusy(false);
    if (!r.success) { say(r.error || 'Could not save', 'bad'); return; }
    setForm({ ...blank });
    setAdding(false);
    say(form.id ? 'Job updated' : 'Job saved');
    reload();
  }

  async function remove(row) {
    const r = await post('softDelete', { kind: 'income', id: row.id });
    if (!r.success) { say(r.error || 'Could not remove', 'bad'); return; }
    reload();
    // Nothing is actually destroyed — the row is stamped, not deleted — so Undo is
    // always honest, and a mis-tap on a phone can never cost a record.
    say(<>Removed. <button onClick={async () => { await post('undoDelete', { kind: 'income', id: row.id }); reload(); }} style={{ ...S.ghost, padding: '3px 9px', marginLeft: 6 }}><Undo2 size={12} /> Undo</button></>);
  }

  // Grouped by day, newest first — the way she will look for a job she half-remembers.
  const byDay = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (!m.has(r.jobDate)) m.set(r.jobDate, []); m.get(r.jobDate).push(r); });
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 18, alignItems: 'baseline' }}>
        <div>
          <div style={S.label}>Today</div>
          <div style={{ ...S.amount, fontSize: '1.6rem', color: ACCENT }}>{money(todayTotal)}</div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={S.label}>This year (FY {fy})</div>
          <div style={{ ...S.amount, fontSize: '1.15rem' }}>{money(watch.turnover)}</div>
        </div>
      </div>

      {!adding && (
        <button onClick={() => { setForm({ ...blank }); setAdding(true); }} style={{ ...S.bigBtn(ACCENT), marginBottom: 16 }}>
          <Plus size={19} /> Add a job
        </button>
      )}

      {adding && (
        <div style={S.card}>
          <h2 style={S.h2}>{form.id ? 'Edit job' : 'New job'}</h2>
          <p style={S.sub}>Whatever you charged the client, as they paid it.</p>
          <label style={S.label}>Date</label>
          <input style={S.input} type="date" value={form.jobDate} onChange={(e) => setForm({ ...form, jobDate: e.target.value })} />
          <label style={S.label}>Client</label>
          <input style={S.input} value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="First name is enough" />
          <label style={S.label}>What you did</label>
          <input style={S.input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. full set, gel, some art" />
          <label style={S.label}>Amount</label>
          <input style={S.input} type="number" inputMode="decimal" step="0.01" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
          <label style={S.label}>Paid by</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {PAYMENT_METHODS.map((m) => (
              <button key={m.value} onClick={() => setForm({ ...form, paymentMethod: m.value })} style={S.chip(form.paymentMethod === m.value)}>{m.label}</button>
            ))}
          </div>
          <label style={S.label}>Note (optional)</label>
          <input style={S.input} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="anything worth remembering" />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={save} disabled={busy} style={{ ...S.bigBtn(ACCENT), flex: 2 }}>
              {busy ? <Loader2 size={17} className="spin" /> : <Check size={17} />} Save
            </button>
            <button onClick={() => setAdding(false)} style={{ ...S.ghost, flex: 1, justifyContent: 'center' }}><X size={15} /> Cancel</button>
          </div>
        </div>
      )}

      {byDay.length === 0 && <div style={{ ...S.card, textAlign: 'center', ...S.dim }}>No jobs recorded yet.</div>}

      {byDay.map(([date, list]) => (
        <div key={date} style={S.card}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h2 style={S.h2}>{dateAu(date)}</h2>
            <span style={{ ...S.amount, marginLeft: 'auto', color: ACCENT }}>{money(sum(list, (r) => r.amount))}</span>
          </div>
          {list.map((r) => (
            <div key={r.id} style={S.row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{r.client || 'Client'}</div>
                <div style={S.dim}>{[r.description, (PAYMENT_METHODS.find((m) => m.value === r.paymentMethod) || {}).label].filter(Boolean).join(' · ')}</div>
                {r.note && <div style={{ ...S.dim, fontStyle: 'italic' }}>{r.note}</div>}
              </div>
              <span style={S.amount}>{money(r.amount)}</span>
              <button onClick={() => openEdit(r)} style={{ ...S.ghost, padding: '7px 9px' }} title="Edit"><Pencil size={14} /></button>
              <button onClick={() => remove(r)} style={{ ...S.ghost, padding: '7px 9px' }} title="Remove"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Spending — what she paid out, with the docket attached
function SpendingTab({ books, reload, say }) {
  const [adding, setAdding] = useState(false);
  const blank = { spendDate: today(), supplier: '', category: '', amount: '', note: '' };
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);       // { base64, contentType, filename }
  const [hadReceipt, setHadReceipt] = useState(false);
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const rows = books.expenses;
  const noReceipt = rows.filter((r) => !r.hasReceipt);

  // The whole point of the "no receipt attached" chase is being able to do something
  // about it, so opening a row for edit is how a receipt reaches an expense that was
  // saved without one — as well as how a wrong amount gets corrected.
  const openEdit = (r) => {
    setForm({ id: r.id, spendDate: r.spendDate, supplier: r.supplier, category: r.category, amount: String(r.amount ?? ''), note: r.note });
    setFile(null);
    setHadReceipt(!!r.hasReceipt);
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Photo first: read it for prefill, but never block the save on that read.
  async function pick(e) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const up = await fileToUpload(f);
      setFile(up);
      setReading(true);
      const r = await post('ocrReceiptImage', { base64: up.base64, contentType: up.contentType });
      setReading(false);
      const x = (r.success && r.extracted) || null;
      // A read that comes back with every field blank is Claude saying it could not make
      // the receipt out — the call succeeded, the reading did not. Reporting that as
      // "read the receipt" would have her trusting a form it never actually filled.
      const gotSomething = !!x && !!(x.vendor || x.date || Number(x.amount) > 0 || x.items);
      if (x) {
        setForm((cur) => ({
          ...cur,
          supplier: cur.supplier || x.vendor || '',
          spendDate: /^\d{4}-\d{2}-\d{2}$/.test(x.date || '') ? x.date : cur.spendDate,
          amount: cur.amount || (Number(x.amount) > 0 ? String(x.amount) : ''),
          note: cur.note || x.items || '',
        }));
      }
      if (gotSomething) say('Read the receipt — check the details before saving');
      else say('Photo attached, but I couldn’t make the writing out — type the details in yourself.', 'bad');
    } catch (e2) { setReading(false); say(e2.message, 'bad'); }
  }

  async function save() {
    if (!form.amount) { say('How much was it?', 'bad'); return; }
    setBusy(true);
    const r = await post('saveExpense', {
      ...form,
      fileBase64: file?.base64,
      fileContentType: file?.contentType,
      // Keep the extension: without it the stored file downloads as a name the operating
      // system can't open, which is a poor thing to discover when the accountant asks.
      fileName: file
        ? `${(form.supplier || 'receipt').replace(/[^\w.\- ]/g, '_')}_${form.spendDate || ''}.${file.contentType === 'application/pdf' ? 'pdf' : 'jpg'}`
        : undefined,
      extracted: undefined,
    });
    setBusy(false);
    if (!r.success) { say(r.error || 'Could not save', 'bad'); return; }
    const wasEdit = !!form.id;
    setForm({ ...blank }); setFile(null); setHadReceipt(false); setAdding(false);
    if (file) say(wasEdit ? 'Updated, receipt attached' : 'Saved with the receipt');
    else if (hadReceipt) say('Updated');
    else say('Saved — no receipt on this one yet. Tap it in the list to add the photo when you find it.');
    reload();
  }

  async function remove(row) {
    const r = await post('softDelete', { kind: 'expense', id: row.id });
    if (!r.success) { say(r.error || 'Could not remove', 'bad'); return; }
    reload();
    say(<>Removed. <button onClick={async () => { await post('undoDelete', { kind: 'expense', id: row.id }); reload(); }} style={{ ...S.ghost, padding: '3px 9px', marginLeft: 6 }}><Undo2 size={12} /> Undo</button></>);
  }

  return (
    <>
      {noReceipt.length > 0 && (
        <div style={{ ...S.card, borderColor: WARN, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={17} style={{ color: WARN, flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.86rem' }}>
            <strong>{noReceipt.length} {noReceipt.length === 1 ? 'expense has' : 'expenses have'} no receipt attached.</strong>
            <div style={S.dim}>Add the photo when you find it — a claim without a receipt behind it is the one that causes trouble later.</div>
          </div>
        </div>
      )}

      {!adding && (
        <button onClick={() => { setForm({ ...blank }); setFile(null); setAdding(true); }} style={{ ...S.bigBtn(ACCENT), marginBottom: 16 }}>
          <Plus size={19} /> Add an expense
        </button>
      )}

      {adding && (
        <div style={S.card}>
          <h2 style={S.h2}>{form.id ? 'Edit expense' : 'New expense'}</h2>
          <p style={S.sub}>Snap the receipt first and the details fill themselves in. Enter the total you actually paid.</p>

          <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" onChange={pick} style={{ display: 'none' }} />
          <button onClick={() => fileRef.current?.click()} style={{ ...S.bigBtn(file ? GOOD : 'var(--hp-card, #1E1E24)', file ? '#141418' : 'var(--hp-text, #EDEDF0)'), marginBottom: 16, border: '1px solid var(--hp-border, #2A2A32)' }}>
            {reading ? <><Loader2 size={17} className="spin" /> Reading the receipt…</>
              : file ? <><Check size={17} /> Receipt attached — tap to replace</>
              : hadReceipt ? <><Camera size={19} /> Replace the receipt</>
              : <><Camera size={19} /> Photograph the receipt</>}
          </button>

          <label style={S.label}>Date</label>
          <input style={S.input} type="date" value={form.spendDate} onChange={(e) => setForm({ ...form, spendDate: e.target.value })} />
          {form.spendDate && form.spendDate < TRADING_START && (
            <p style={{ ...S.sub, color: WARN, marginTop: -8 }}>
              This is before you started on {dateAu(TRADING_START)}, so it goes on the separate start-up list for the accountant. Keep the real date — that is what makes it useful.
            </p>
          )}
          <label style={S.label}>Who you paid</label>
          <input style={S.input} value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="shop or supplier" />
          <label style={S.label}>Amount paid</label>
          <input style={S.input} type="number" inputMode="decimal" step="0.01" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
          <label style={S.label}>What kind of cost</label>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
            {EXPENSE_CATEGORIES.map((c) => (
              <button key={c} onClick={() => setForm({ ...form, category: c })} style={{ ...S.chip(form.category === c), fontSize: '.76rem', padding: '7px 11px' }}>{c}</button>
            ))}
          </div>
          <label style={S.label}>Note (optional)</label>
          <input style={S.input} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="what it was for" />

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={save} disabled={busy} style={{ ...S.bigBtn(ACCENT), flex: 2 }}>
              {busy ? <Loader2 size={17} className="spin" /> : <Check size={17} />} Save
            </button>
            <button onClick={() => { setAdding(false); setFile(null); setHadReceipt(false); setForm({ ...blank }); }} style={{ ...S.ghost, flex: 1, justifyContent: 'center' }}><X size={15} /> Cancel</button>
          </div>
        </div>
      )}

      {rows.length === 0 && <div style={{ ...S.card, textAlign: 'center', ...S.dim }}>No expenses recorded yet.</div>}

      {rows.length > 0 && (
        <div style={S.card}>
          <h2 style={S.h2}>Everything you have spent</h2>
          <p style={S.sub}>{rows.length} {rows.length === 1 ? 'entry' : 'entries'} · {money(sum(rows, (r) => r.amount))} in total</p>
          {rows.map((r) => (
            <div key={r.id} style={S.row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{r.supplier || 'Expense'}</div>
                <div style={S.dim}>{[dateAu(r.spendDate), r.category].filter(Boolean).join(' · ')}</div>
                {r.note && <div style={{ ...S.dim, fontStyle: 'italic' }}>{r.note}</div>}
                {!r.hasReceipt && (
                  <button onClick={() => openEdit(r)} style={{ ...S.dim, color: WARN, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: '.8rem', textDecoration: 'underline' }}>
                    No receipt attached — add the photo
                  </button>
                )}
                {r.ocrError && <div style={{ ...S.dim, color: WARN }}>Could not auto-read this one</div>}
              </div>
              <span style={S.amount}>{money(r.amount)}</span>
              {r.hasReceipt && (
                <a href={r.fileViewUrl} target="_blank" rel="noreferrer" style={{ ...S.ghost, padding: '7px 9px' }} title="See the receipt"><FileText size={14} /></a>
              )}
              <button onClick={() => openEdit(r)} style={{ ...S.ghost, padding: '7px 9px' }} title="Edit"><Pencil size={14} /></button>
              <button onClick={() => remove(r)} style={{ ...S.ghost, padding: '7px 9px' }} title="Remove"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Reports — the P&L, and the pack the accountant gets
function ReportsTab({ books, say, reload }) {
  const fys = availableFys(books.income, books.expenses);
  const [period, setPeriod] = useState(fys[0] || currentFy());
  // A custom range starts at the current financial year up to today, so choosing it
  // never lands on an empty screen that looks like missing data.
  const [custom, setCustom] = useState({ from: fyRange(currentFy()).from, to: today() });
  const [email, setEmail] = useState(books.accountantEmail || '');
  const [sending, setSending] = useState(false);

  useEffect(() => { setEmail(books.accountantEmail || ''); }, [books.accountantEmail]);

  const backwards = period === 'custom' && custom.from && custom.to && custom.from > custom.to;
  const range = period === 'all' ? { from: '', to: '' }
    : period === 'custom' ? { from: custom.from, to: custom.to }
    : fyRange(period);
  const label = period === 'all' ? 'All time'
    : period === 'custom' ? `${dateAu(custom.from) || 'the start'} – ${dateAu(custom.to) || 'today'}`
    : `FY ${period}`;
  // `range` is derived from `period` and the custom dates, so those are the dependency.
  const s = useMemo(() => summarise(books.income, books.expenses, range), [books, period, custom.from, custom.to]);
  const pre = preStartExpenses(books.expenses);
  // The threshold is a FINANCIAL-YEAR question, never a custom-range one — showing
  // "turnover" for an arbitrary window against a yearly threshold would be misleading.
  // So a custom range still reports the threshold against the FY its start falls in.
  const watchFy = (period === 'all' || period === 'custom') ? (fyOf(custom.from) || currentFy()) : period;
  const watch = gstWatch(books.income, watchFy);

  function download() {
    try {
      const name = exportBooks({ income: books.income, expenses: books.expenses, period: range, periodLabel: label });
      say(`Downloaded ${name}`);
    } catch (e) { say(e.message, 'bad'); }
  }

  async function sendToAccountant() {
    if (!email) { say('Put the accountant’s email address in first', 'bad'); return; }
    setSending(true);
    try {
      const r = await post('emailPack', {
        to: email, remember: true, periodLabel: label,
        filename: booksFilename(label),
        workbookBase64: workbookBase64({ income: books.income, expenses: books.expenses, period: range, periodLabel: label }),
      });
      if (!r.success) throw new Error(r.error || 'Could not send');
      say(`Sent to ${email}`);
      reload();
    } catch (e) { say(e.message, 'bad'); }
    setSending(false);
  }

  return (
    <>
      <div style={S.card}>
        <label style={S.label} htmlFor="nb-period">Period</label>
        <select id="nb-period" style={{ ...S.input, marginBottom: period === 'custom' ? 14 : 0 }} value={period} onChange={(e) => setPeriod(e.target.value)}>
          {fys.map((f) => <option key={f} value={f}>{fyLabel(f)}</option>)}
          <option value="custom">Choose your own dates…</option>
          <option value="all">All time</option>
        </select>
        {period === 'custom' && (
          <>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={S.label} htmlFor="nb-from">From</label>
                <input id="nb-from" style={{ ...S.input, marginBottom: 0 }} type="date" value={custom.from}
                  onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label} htmlFor="nb-to">To</label>
                <input id="nb-to" style={{ ...S.input, marginBottom: 0 }} type="date" value={custom.to}
                  onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
              </div>
            </div>
            {/* Both ends are inclusive, and a backwards range would silently total zero —
                which reads exactly like a quiet month. Say so instead. */}
            {backwards
              ? <p style={{ ...S.sub, color: WARN, margin: '10px 0 0' }}>The From date is after the To date, so nothing falls inside it. Swap them.</p>
              : <p style={{ ...S.sub, margin: '10px 0 0' }}>Both days are included. Everything below — and the spreadsheet — follows these dates.</p>}
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 12 }}>
              {QUICK_RANGES.map((q) => {
                const r = q.range();
                const on = custom.from === r.from && custom.to === r.to;
                return <button key={q.label} onClick={() => setCustom(r)} style={{ ...S.chip(on), fontSize: '.76rem', padding: '7px 11px' }}>{q.label}</button>;
              })}
            </div>
          </>
        )}
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>Profit &amp; loss — {label}</h2>
        <p style={S.sub}>Not registered for GST, so this is simply money in less money out.</p>
        <div style={S.row}><span style={{ flex: 1 }}>Income from {s.income.count} {s.income.count === 1 ? 'job' : 'jobs'}</span><span style={{ ...S.amount, color: GOOD }}>{money(s.income.total)}</span></div>
        <div style={S.row}><span style={{ flex: 1 }}>Expenses</span><span style={{ ...S.amount, color: BAD }}>−{money(s.expenses.total)}</span></div>
        <div style={{ ...S.row, borderTopWidth: 2 }}>
          <span style={{ flex: 1, fontWeight: 800 }}>Net</span>
          <span style={{ ...S.amount, fontSize: '1.3rem', color: s.net >= 0 ? GOOD : BAD }}>{money(s.net)}</span>
        </div>
        {s.income.count > 0 && <div style={{ ...S.dim, marginTop: 10 }}>Average per job {money(s.averageJob)}</div>}
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>Turnover against the GST threshold — FY {watchFy}</h2>
        <p style={S.sub}>
          Registering for GST becomes compulsory once turnover reaches {money(watch.threshold)} in a 12-month period, so this always counts a full financial year rather than whatever period you picked above. It is here so it is never a surprise — your accountant advises on the timing.
        </p>
        <div style={{ height: 10, borderRadius: 999, background: 'var(--hp-card, #1E1E24)', overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ width: `${watch.percent}%`, height: '100%', background: watch.percent > 80 ? WARN : ACCENT }} />
        </div>
        <div style={{ display: 'flex', fontSize: '.84rem' }}>
          <span style={S.amount}>{money(watch.turnover)}</span>
          <span style={{ ...S.dim, marginLeft: 'auto' }}>{watch.reached ? 'Threshold reached — speak to the accountant' : `${money(watch.remaining)} to go`}</span>
        </div>
      </div>

      {s.expenses.byCategory.length > 0 && (
        <div style={S.card}>
          <h2 style={S.h2}>Where the money went</h2>
          {s.expenses.byCategory.map((g) => (
            <div key={g.key} style={S.row}>
              <span style={{ flex: 1 }}>{g.key}</span>
              <span style={S.dim}>{g.count}</span>
              <span style={S.amount}>{money(g.total)}</span>
            </div>
          ))}
        </div>
      )}

      {s.byMonth.length > 0 && (
        <div style={S.card}>
          <h2 style={S.h2}>Month by month</h2>
          {s.byMonth.map((m) => (
            <div key={m.month} style={S.row}>
              <span style={{ flex: 1 }}>{monthName(m.month)}</span>
              <span style={{ ...S.amount, color: GOOD, fontSize: '.84rem' }}>{money(m.income)}</span>
              <span style={{ ...S.amount, color: BAD, fontSize: '.84rem' }}>−{money(m.expenses)}</span>
              <span style={{ ...S.amount, width: 84, textAlign: 'right' }}>{money(m.net)}</span>
            </div>
          ))}
        </div>
      )}

      {s.expenses.missingReceipts.length > 0 && (
        <div style={{ ...S.card, borderColor: WARN }}>
          <h2 style={{ ...S.h2, color: WARN }}>Expenses with no receipt</h2>
          <p style={S.sub}>Chase these before the year closes.</p>
          {s.expenses.missingReceipts.map((r) => (
            <div key={r.id} style={S.row}>
              <span style={{ flex: 1 }}>{r.supplier || 'Expense'} <span style={S.dim}>{dateAu(r.spendDate)}</span></span>
              <span style={S.amount}>{money(r.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {pre.length > 0 && (
        <div style={S.card}>
          <h2 style={S.h2}>Before you started — {money(sum(pre, (r) => r.amount))}</h2>
          <p style={S.sub}>
            {pre.length} {pre.length === 1 ? 'cost' : 'costs'} paid before {dateAu(TRADING_START)}. They sit in the previous financial year, so they are listed on their own sheet in the accountant’s pack rather than mixed into a trading year — how they are treated is their call, not this app’s.
          </p>
          {pre.map((r) => (
            <div key={r.id} style={S.row}>
              <span style={{ flex: 1 }}>{r.supplier || 'Expense'} <span style={S.dim}>{dateAu(r.spendDate)}</span></span>
              {!r.hasReceipt && <span style={{ ...S.dim, color: WARN }}>no receipt</span>}
              <span style={S.amount}>{money(r.amount)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={S.card}>
        <h2 style={S.h2}>Send it to the accountant</h2>
        <p style={S.sub}>
          One spreadsheet: a summary, every job, every expense, and the start-up costs on their own sheet. The receipts themselves stay here and can be sent whenever they ask.
        </p>
        <button onClick={download} style={{ ...S.bigBtn(ACCENT), marginBottom: 12 }}><Download size={18} /> Download the spreadsheet</button>
        <label style={S.label}>Accountant’s email</label>
        <input style={S.input} type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@accountants.com.au" />
        <button onClick={sendToAccountant} disabled={sending} style={{ ...S.bigBtn('var(--hp-card, #1E1E24)', 'var(--hp-text, #EDEDF0)'), border: '1px solid var(--hp-border, #2A2A32)' }}>
          {sending ? <Loader2 size={17} className="spin" /> : <Mail size={17} />} Email it to them
        </button>
      </div>

      <div style={{ ...S.card, background: 'transparent' }}>
        <div style={{ ...S.dim, fontSize: '.75rem', lineHeight: 1.6 }}>
          <TrendingUp size={13} style={{ verticalAlign: '-2px', marginRight: 5 }} />
          Asha Bouveng · ABN 60 119 559 804 · sole trader, not registered for GST. Records are kept here and nothing is ever really deleted — a removed entry is only hidden, so it can always be brought back.
        </div>
      </div>
    </>
  );
}
